"use client";
import React, { useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import { useHomeLanguage } from "./HomeLanguageContext";

const heroImages = [
  {
    src: "/assets/optimized/kolabo-hrm-1-42f33aaa23.webp",
    alt: "Kolabo HRM dashboard",
    label: "HRM",
  },
  {
    src: "/assets/optimized/kolabo-app-merge-d79a507c82.webp",
    alt: "Kolabo app merge",
    label: "App",
  },
  {
    src: "/assets/optimized/kolabo-accounting-2-5c2395a467.webp",
    alt: "Kolabo accounting",
    label: "Accounting",
  },
  {
    src: "/assets/optimized/kolabo-crm-f0f6efecc6.webp",
    alt: "Kolabo CRM",
    label: "CRM",
  },
  {
    src: "/assets/optimized/kolabo-hrm9-e0bcbc059e.webp?v=111",
    alt: "Kolabo Project Management",
    label: "Project",
  },
];

export default function HeroSection() {
  const { lang } = useHomeLanguage();
  const sectionRef = useRef<HTMLElement>(null);
  const mousePos = { x: 0, y: 0 };

  const stats = [
    { value: lang === "id" ? "Ratusan" : "Hundreds", label: lang === "id" ? "Modul Terintegrasi" : "Integrated Modules" },
    { value: "500rb", label: lang === "id" ? "Per Bulan" : "Per Month" },
    { value: "300+", label: lang === "id" ? "Perusahaan" : "Companies" },
  ];

  const floatingTags = [
    { label: "HRM & Payroll", x: "2%", y: "35%", delay: "0s" },
    { label: "Accounting", x: "2%", y: "50%", delay: "1.5s" },
    { label: "CRM", x: "88%", y: "30%", delay: "3s" },
    { label: "Project Management", x: "88%", y: "55%", delay: "2s" },
    { label: "AI Assistant", x: "42%", y: "92%", delay: "4s" },
  ];

  const copy = {
    subtitle:
      lang === "id"
        ? "Kelola operasional bisnis dari satu platform: HRM, HRIS, absensi, payroll, accounting, CRM, project management dan POS yang terintegrasi real-time."
        : "Run your entire business operations from one platform: HRM, HRIS, attendance, payroll, accounting, CRM, and project management with real-time integration.",
    trial: lang === "id" ? "Coba Gratis, Tanpa Ribet!" : "Try Free, No Hassle!",
    explore: lang === "id" ? "Jelajahi Kolabo" : "Explore Kolabo",
    trialNote:
      lang === "id"
        ? "Coba Gratis Tanpa Ribet, Tanpa Kewajiban Berlangganan Dalam Hitungan Detik!"
        : "Try free in seconds, with no subscription obligation.",
    modulesBadge: lang === "id" ? "✨ Ratusan Modul" : "✨ Hundreds of Modules",
    scroll: lang === "id" ? "Scroll" : "Scroll",
  };

  return (
    <section
      id="home"
      ref={sectionRef}
      className="relative min-h-screen flex flex-col justify-center overflow-hidden mesh-bg"
    >
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <div
          className="orb-1 absolute w-[600px] h-[600px] rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, rgba(23,182,186,0.6) 0%, transparent 70%)",
            top: "-10%",
            left: "-5%",
            transform: `translate(${mousePos.x * -20}px, ${mousePos.y * -15}px)`,
            transition: "transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
        <div
          className="orb-2 absolute w-[500px] h-[500px] rounded-full opacity-15"
          style={{
            background: "radial-gradient(circle, rgba(161,209,134,0.5) 0%, transparent 70%)",
            bottom: "0%",
            right: "-5%",
            transform: `translate(${mousePos.x * 15}px, ${mousePos.y * 20}px)`,
            transition: "transform 1s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
        <div
          className="orb-3 absolute w-[400px] h-[400px] rounded-full opacity-10"
          style={{
            background: "radial-gradient(circle, rgba(28,200,140,0.5) 0%, transparent 70%)",
            top: "40%",
            left: "40%",
            transform: `translate(${mousePos.x * 10}px, ${mousePos.y * -10}px)`,
            transition: "transform 1.2s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        />
      </div>

      <div className="absolute inset-0 z-0 dot-pattern opacity-30 pointer-events-none" />

      <div className="absolute inset-0 flex justify-between pointer-events-none z-0 px-8 md:px-24 overflow-hidden">
        <div className="relative w-px h-full bg-white/5 overflow-hidden hidden md:block">
          <div className="beam beam-d0" />
        </div>
        <div className="relative w-px h-full bg-white/5 overflow-hidden hidden md:block">
          <div className="beam beam-d1" />
        </div>
        <div className="relative w-px h-full bg-white/5 overflow-hidden hidden md:block">
          <div className="beam beam-d2" />
        </div>
        <div className="relative w-px h-full bg-white/5 overflow-hidden hidden md:block">
          <div className="beam beam-d3" />
        </div>
      </div>

      <div className="absolute inset-0 z-[70] pointer-events-none hidden xl:block">
        {floatingTags.map((tag) => (
          <div
            key={tag.label}
            className="absolute hero-tag-float z-[80]"
            style={{ left: tag.x, top: tag.y, animationDelay: tag.delay }}
          >
            <div className="px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-md border border-primary/15 text-fg/70 text-[11px] font-semibold whitespace-nowrap">
              {tag.label}
            </div>
          </div>
        ))}
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-bg-light to-transparent z-10 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24">
        <div className="grid lg:grid-cols-12 gap-8 xl:gap-12 items-center">
          <div className="lg:col-span-6 flex flex-col gap-6 lg:gap-7">
            <div style={{ animation: "animationIn 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both" }} className="overflow-visible">
              <h1 className="-ml-[2px] font-heading text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-[4rem] font-bold text-fg leading-[1.2] tracking-[-0.01em] overflow-visible pb-0">
                <span className="block">
                  Kolabo - <span className="shimmer-text">Business</span>
                </span>
                <span className="block -ml-[4px] -mt-1 pb-[0.2em] pr-[0.12em] overflow-visible leading-[1.12]">
                  <span className="gradient-text-hero italic font-light inline-block pr-[0.06em] pt-[0.02em]">Operating</span>{" "}
                  <span className="gradient-text-hero italic font-light inline-block pr-[0.04em]">System</span>
                </span>
              </h1>
              <p className="mt-0 text-[1.4rem] font-normal text-fg/72 tracking-normal leading-[1.32]">
                HRM · Accounting · CRM · Project Management · POS
              </p>
            </div>

            <p
              className="mt-0 text-fg/80 text-[15px] lg:text-base font-light leading-[1.65] max-w-xl"
              style={{ animation: "animationIn 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.35s both" }}
            >
              {copy.subtitle}
            </p>

            <div
              className="mt-1.5 flex flex-col sm:flex-row gap-4"
              style={{ animation: "animationIn 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.5s both" }}
            >
              <a
                href="#daftar"
                className="btn-gradient text-white font-bold px-8 py-4 rounded-full shadow-brand-lg text-center flex items-center justify-center gap-2.5 text-[15px] magnetic-btn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
                {copy.trial}
              </a>
              <a
                href="https://www.kolabo.id/Kolabo%20Overview%202026%20BAHASA%20INDONESIA%20COMPRESSED.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white/75 hover:bg-white backdrop-blur-md text-fg font-semibold px-8 py-4 rounded-full border border-primary/20 hover:border-primary/35 text-center flex items-center justify-center gap-2.5 text-[15px] transition-all duration-400"
              >
                {copy.explore}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="group-hover:translate-x-1 transition-transform duration-300"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>

            <p
              className="mt-0.5 text-fg/60 text-xs flex items-center gap-2 font-medium"
              style={{ animation: "animationIn 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.65s both" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              {copy.trialNote}
            </p>

            <div className="flex items-center gap-6 sm:gap-8 pt-1.5">
              {stats.map((stat) => (
                <div key={stat.label} className="stat-item flex flex-col gap-1">
                  <span className="font-heading text-2xl font-bold text-fg tracking-tight">{stat.value}</span>
                  <span className="text-fg/60 text-[11px] font-medium uppercase tracking-wider">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="lg:col-span-6 relative z-30 mt-8 lg:mt-0"
            style={{ animation: "animationIn 1s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both" }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/10 rounded-3xl blur-3xl scale-110 pointer-events-none" />

            <div className="relative grid grid-cols-2 gap-3 pt-6">
              {heroImages.map((img, i) => (
                <div
                  key={img.src}
                  className={`relative rounded-2xl overflow-hidden border border-white/15 shadow-2xl spotlight-card ${
                    i === 0 ? "col-span-2 h-52 sm:h-60" : "h-36 sm:h-44"
                  } ${
                    i === 0
                      ? ""
                      : i === 1
                        ? "hero-sub-image-a"
                        : i === 2
                          ? "hero-sub-image-b"
                          : i === 3
                            ? "hero-sub-image-c"
                            : "hero-sub-image-d"
                  }`}
                >
                  <AppImage
                    src={img.src}
                    alt={img.alt}
                    fill
                    className="object-cover transition-transform duration-700 hover:scale-105"
                    sizes={i === 0 ? "(max-width: 1024px) 100vw, 50vw" : "(max-width: 1024px) 50vw, 25vw"}
                    priority={i === 0}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/70 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cta pulse-dot" />
                    <span className="text-white text-[10px] font-bold uppercase tracking-widest bg-black/30 backdrop-blur-sm px-2.5 py-1 rounded-full border border-white/10">
                      {img.label}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="absolute top-0 right-0 bg-gradient-brand-135 text-white text-xs font-bold px-4 py-2 rounded-full shadow-brand hero-tag-float border border-white/20 z-[85] whitespace-nowrap">
              {copy.modulesBadge}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-fg/50">
        <span className="text-[10px] uppercase tracking-[0.25em] font-semibold">{copy.scroll}</span>
        <div className="scroll-indicator w-5 h-8 rounded-full border border-fg/25 flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 rounded-full bg-fg/60" />
        </div>
      </div>
    </section>
  );
}
